/**
 * Product-first routing cho task pane.
 *
 * Add-in COM là "shell": manifest luôn trỏ vào đây. Shell quyết định ở lại COM,
 * nạp add-in ENT (Module Federation remote, khác origin) vào chính task pane này,
 * hoặc hiện màn chọn sản phẩm. Task pane không điều hướng sang origin khác nên manifest
 * không cần khai báo thêm AppDomain; Office.js do shell nạp một lần.
 */

export type ProductId = "com" | "ent";

export type ProductRoute =
  | { kind: "com" }
  /** `requireSession`: chỉ vào ENT nếu ENT còn phiên, ngược lại về màn chọn (ENT tự kiểm tra). */
  | { kind: "ent"; requireSession: boolean }
  | { kind: "picker" };

export type ResolveProductRouteInput = {
  search: string;
  storedProduct: ProductId | null;
  hasComSession: boolean;
  /** false = chưa cấu hình remote ENT, shell giữ nguyên hành vi cũ (chỉ COM). */
  entEnabled: boolean;
};

/** `?product=com|ent` — lựa chọn tường minh (màn chọn, nút ribbon, deep link). */
export const PRODUCT_PARAM = "product";
/** `?switch=1` — luôn hiện màn chọn (shell tự gắn sau khi đăng xuất). */
export const SWITCH_PARAM = "switch";

export function parseProductId(value: string | null | undefined): ProductId | null {
  const normalized = value?.trim().toLowerCase();
  return normalized === "com" || normalized === "ent" ? normalized : null;
}

/**
 * Thứ tự ưu tiên:
 * 1. `?switch=1` → màn chọn (sau khi đăng xuất ở COM hoặc ENT).
 * 2. `?product=` → vào thẳng sản phẩm đó (người dùng vừa chọn).
 * 3. Sản phẩm đã ghi nhớ và còn phiên → vào sản phẩm đó.
 * 4. Còn phiên COM (người dùng cũ, chưa ghi nhớ) → COM.
 * 5. Chưa đăng nhập → màn chọn.
 */
export function resolveProductRoute(input: ResolveProductRouteInput): ProductRoute {
  if (!input.entEnabled) {
    return { kind: "com" };
  }
  const params = new URLSearchParams(input.search);
  if (params.has(SWITCH_PARAM)) {
    return { kind: "picker" };
  }

  const requested = parseProductId(params.get(PRODUCT_PARAM));
  if (requested === "ent") {
    return { kind: "ent", requireSession: false };
  }
  if (requested === "com") {
    return { kind: "com" };
  }

  if (input.storedProduct === "ent") {
    return { kind: "ent", requireSession: true };
  }
  return input.hasComSession ? { kind: "com" } : { kind: "picker" };
}

/**
 * Query của shell cho đích mới, bỏ tham số shell cũ; giữ query Office (vd. `_host_Info`).
 */
export function buildShellSearch(search: string, target: { product: ProductId } | "picker"): string {
  const params = new URLSearchParams(search);
  params.delete(PRODUCT_PARAM);
  params.delete(SWITCH_PARAM);
  if (target === "picker") {
    params.set(SWITCH_PARAM, "1");
  } else {
    params.set(PRODUCT_PARAM, target.product);
  }
  return `?${params.toString()}`;
}
